import { authedFetch } from "../../utils/authedFetch";
import React, { useMemo, useRef, useState } from "react";
import EditableSelect from "../lockbox-inventory/editable-select";
import AssignedLockbox from "./assigned-lockbox";
import {
  Calendar,
  CalendarPlus,
  Loader2,
  LockKeyholeOpen,
  Mail,
  Plus,
  X,
} from "lucide-react";
import ClientSuggesstionPopup from "../ClientSuggesstionPopup";
import MaskedField from "../lockbox-inventory/mask-field";
import { formatCurrency, formatDate, formatDates, formatNumber } from "../../utils/helper";
import { buildAddToCalendarUrl } from "../../utils/importantDates";
import {
  getContactsByIds,
  updateDealById,
  updateElectronicLockboxById,
  updatePushButtonLockboxById,
} from "../../utils/firebasefunctions";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { canRequestStaging } from "../../utils/stagingIntake";
import RequestStagingBidModal from "./RequestStagingBidModal";
import {
  DEFAULT_TEMPLATES,
  lockboxLine,
  formatClientsList,
  stripEmptyFactLines,
} from "../../utils/openHouseEmails";
import { interpolate } from "../../utils/emailPlaceholders";
import {
  buildBanner,
  buildSignature,
  buildFooter,
  wrapEmail,
  paragraphsToHtml,
} from "../../utils/openHouseEmailsHtml";
import AddClientPicker from "../new-contact/AddClientPicker";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import { addContact } from "../../utils/contactsApi";
import CollapsibleSection from "./CollapsibleSection";

// ============ Compact cells for the Marketing & Staging cards ============
// Tighter than the global `Field` component, designed to sit inside a 3-column
// grid. Each cell handles its own edit-vs-view switch.

function MiniTextCell({ label, field, value, isEdit, onChange }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-1">{label}</div>
      {isEdit ? (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(field, e.target.value)}
          className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-[#1E293B] outline-none"
        />
      ) : (
        <div
          className={`text-sm ${value ? "font-semibold text-slate-900" : "text-slate-400 italic"}`}
        >
          {value || "Not set"}
        </div>
      )}
    </div>
  );
}

function MiniDateCell({ label, field, value, isEdit, onChange }) {
  const hiddenDateRef = useRef(null);
  const openPicker = () => {
    if (hiddenDateRef.current) {
      hiddenDateRef.current.showPicker
        ? hiddenDateRef.current.showPicker()
        : hiddenDateRef.current.click();
    }
  };
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-1">{label}</div>
      {isEdit ? (
        <div className="relative">
          <input
            type="text"
            placeholder="MM/DD/YYYY"
            value={formatDates(value)}
            readOnly
            onClick={openPicker}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm cursor-pointer focus:border-[#1E293B] outline-none"
          />
          <div
            className="absolute top-2 right-2 cursor-pointer text-slate-500"
            onClick={openPicker}
          >
            <Calendar size={14} />
          </div>
          <input
            type="date"
            ref={hiddenDateRef}
            value={value || ""}
            onChange={(e) => onChange(field, e.target.value)}
            className="absolute left-0 top-0 w-0 h-0 opacity-0 pointer-events-none"
          />
        </div>
      ) : (
        <div
          className={`text-sm ${value ? "font-semibold text-slate-900" : "text-slate-400 italic"}`}
        >
          {value ? formatDates(value) : "Not set"}
        </div>
      )}
    </div>
  );
}

function MiniSelectCell({
  label,
  value,
  placeholder,
  options,
  isEdit,
  open,
  onToggleOpen,
  onSelect,
}) {
  return (
    <div className="relative">
      <div className="text-[11px] text-slate-500 mb-1">{label}</div>
      {isEdit ? (
        <>
          <div
            onClick={onToggleOpen}
            className={`w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm cursor-pointer ${
              value ? "" : "text-slate-400"
            }`}
          >
            {value || placeholder || "Select"}
          </div>
          <EditableSelect
            style={{ top: "55px", left: "0px" }}
            visible={open}
            activateoutsidecontainerclick={onToggleOpen}
            status={options}
            onClickcorrect={onSelect}
          />
        </>
      ) : (
        <div
          className={`text-sm ${value ? "font-semibold text-slate-900" : "text-slate-400 italic"}`}
        >
          {value || placeholder || "Not set"}
        </div>
      )}
    </div>
  );
}

// One column in the Stage Schedule grid. Shows a date input/value and (in view
// mode) a Notify Vendor button labeled for this stage.
function ScheduleCell({
  label,
  field,
  value,
  canNotify,
  isEdit,
  onChange,
  onNotify,
  busy,
}) {
  return (
    <div className="flex flex-col">
      <MiniDateCell
        label={label}
        field={field}
        value={value}
        isEdit={isEdit}
        onChange={onChange}
      />
      {canNotify && (
        <button
          type="button"
          onClick={onNotify}
          disabled={busy}
          className="mt-2 inline-flex items-center justify-center gap-1.5 text-[11.5px] font-semibold text-slate-700 border border-slate-300 hover:border-themeColor hover:text-themeColor hover:bg-slate-50 rounded-md px-2 py-1 transition w-fit disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:text-slate-700 disabled:hover:bg-transparent"
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Mail size={12} />
          )}
          {busy ? "Sending…" : `Notify · ${label}`}
        </button>
      )}
    </div>
  );
}

// Format a raw numeric string/number as "$1,234,567" for display in
// the currency Field. Mirrors the buyer-form helper.
const formatCurrencyForInput = (val) => {
  if (val === "" || val == null) return "";
  const n = Number(String(val).replace(/[^\d.]/g, ""));
  if (Number.isNaN(n)) return "";
  return "$" + n.toLocaleString("en-US");
};

const parseCurrencyFromInput = (str) => {
  return String(str).replace(/[^\d]/g, "");
};

const Field = React.memo(
  ({
    label,
    field,
    type = "text",
    isBtn = false,
    value,
    onChange,
    isEdit,
    isNumber = false,
    isDate = false,
    notifyvendor,
    dealAddress = "",
  }) => {
    const hiddenDateRef = useRef(null);

    const openDatePicker = () => {
      if (hiddenDateRef.current) {
        hiddenDateRef.current.showPicker
          ? hiddenDateRef.current.showPicker()
          : hiddenDateRef.current.click();
      }
    };

    return (
      <div className="rounded-lg w-full sm:w-[48%]">
        <p className="text-sm text-gray-500">{label}</p>

        {isEdit ? (
          <div className="flex items-center gap-x-2">
            {type === "currency" ? (
              <input
                type="text"
                inputMode="numeric"
                value={formatCurrencyForInput(value)}
                onChange={(e) =>
                  onChange(field, parseCurrencyFromInput(e.target.value))
                }
                className="rounded-lg px-2 py-1.5 w-full text-sm mt-1 border-2 border-gray-300 focus:border-[#1E293B] outline-none"
              />
            ) : type === "date" ? (
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={formatDates(value)}
                  readOnly
                  onClick={openDatePicker}
                  className="rounded-lg px-2 py-1.5 w-full text-sm mt-1 border-2 border-gray-300 focus:border-[#1E293B] outline-none cursor-pointer"
                />

                <div
                  className="absolute top-3 right-3 cursor-pointer"
                  onClick={openDatePicker}
                >
                  <Calendar size={18} />
                </div>

                <input
                  type="date"
                  ref={hiddenDateRef}
                  value={value || ""}
                  onChange={(e) => onChange(field, e.target.value)}
                  className="absolute left-0 top-0 w-0 h-0 opacity-0 pointer-events-none"
                />
              </div>
            ) : (
              <input
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                type={type}
                className="rounded-lg px-2 py-1.5 w-full text-sm mt-1 border-2 focus:border-[#1E293B] outline-none"
              />
            )}

            {(isDate || type === "date") && value && (
              <a
                href={buildAddToCalendarUrl({
                  title: `${label}${dealAddress ? ` — ${dealAddress}` : ""}`,
                  isoDate: value,
                  description: dealAddress ? `Property: ${dealAddress}` : "",
                })}
                target="_blank"
                rel="noreferrer"
                title="Add to Google Calendar"
                className="shrink-0 text-gray-400 hover:text-themeColor transition"
              >
                <CalendarPlus size={18} />
              </a>
            )}

          </div>
        ) : (
             <div className="rounded-lg w-full sm:w-[48%] flex flex-row items-center mt-[10px]">
          <p className="font-semibold">
            {isNumber ? formatCurrency(value) :  isDate ? formatDates(value) : value || "-"}
          </p>
             {(isDate || type === "date") && value && (
              <a
                href={buildAddToCalendarUrl({
                  title: `${label}${dealAddress ? ` — ${dealAddress}` : ""}`,
                  isoDate: value,
                  description: dealAddress ? `Property: ${dealAddress}` : "",
                })}
                target="_blank"
                rel="noreferrer"
                title="Add to Google Calendar"
                className="text-gray-300 hover:text-themeColor transition ml-2"
              >
                <CalendarPlus size={15} />
              </a>
            )}
             {isBtn && (
              <button
              onClick={()=>{
                notifyvendor()
              }}
              className="text-[#475569] w-fit h-full px-2 py-2 hover:border-themeColor hover:text-themeColor hover:bg-gray-100 rounded-lg ml-[10px] flex border border-gray-300 items-center gap-x-1.5">
                <Mail className="size-[14px]" />
                <span className="text-xs font-medium whitespace-nowrap">
                  Notify Vendor
                </span>
              </button>
            )}
            </div>
        )}
      </div>
    );
  },
);

function SellerForm({
  isEdit,
  electronic,
  pushButton,
  formData,
  onFormChange,
  contactType,
  contactSync,
  onContactSelection,
  selectedContacts,
  participants,
  setParticipants,
  dealId,
  onSubmit,
  deal
}) {
  const [openStatus, setOpenStatus] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [openStaging, setOpenStaging] = useState(false);
  // Stager contact picker state (search existing or create new)
  const [stagerPickerOpen, setStagerPickerOpen] = useState(false);
  // Register Redesign intake-form request dialog
  const [stagingBidOpen, setStagingBidOpen] = useState(false);
  const [stagerCreating, setStagerCreating] = useState(false);
  const [stagerSaving, setStagerSaving] = useState(false);
  // Inspector contact picker state — `activeVendorIdx` is the index of the
  // vendor row in inspectionVendors that spawned the picker.
  const [inspectorPickerOpen, setInspectorPickerOpen] = useState(false);
  const [inspectorCreating, setInspectorCreating] = useState(false);
  const [inspectorSaving, setInspectorSaving] = useState(false);
  const [activeVendorIdx, setActiveVendorIdx] = useState(null);
  // Stages the picked vendor before the user clicks Confirm. Two-step flow:
  // pick → preview → confirm. Cleared after send or cancel.
  const [pendingInspectionVendor, setPendingInspectionVendor] =
    useState(null);
  // Photographer contact picker state
  const [photographerPickerOpen, setPhotographerPickerOpen] = useState(false);
  const [photographerCreating, setPhotographerCreating] = useState(false);
  const [photographerSaving, setPhotographerSaving] = useState(false);

  // Which vendor type is currently being notified, so the matching Notify
  // button can switch to an instant "Sending…" state and lock against
  // double-sends until the email request resolves.
  const [notifyingType, setNotifyingType] = useState(null);

  // Contact picker for a deal participant (Closer / Listing Agent / etc.).
  // Holds the participant index the picker / create-drawer is acting on.
  const [participantPickerFor, setParticipantPickerFor] = useState(null);
  const [participantCreatingFor, setParticipantCreatingFor] = useState(null);
  const [participantSaving, setParticipantSaving] = useState(false);
  const Ocuupy = ["Yes", "No"];
  const recission = ["Yes", "No"];
  const [openRecission, setOpenRecission] = useState(false);
  const appraisal = ["Yes", "No", "Waived"];
  const [openAppraisal, setOpenAppraisal] = useState(false);
  const yesNo = ["Yes", "No"];
  const [openInspection, setOpenInspection] = useState(false);
  const stagingOpt = ["None", "Physical", "Blend", "Consult", "Virtual"];
  const [openLockbox, setOpenLockbox] = useState(false);
  const lockboxTypeOptions = ["Push Button", "Electronic"];
  const [openTypeDropdown, setOpenTypeDropdown] = useState(null);
  const [isLoad, setIsLoad] = useState(false);
  // Kept alphabetical by label.
  const inspectionList = [
    { key: "chimney", label: "Chimney" },
    { key: "cityInspection", label: "City Inspection" },
    { key: "fullInspection", label: "Full Inspection" },
    { key: "radon", label: "Radon" },
    { key: "sewerScope", label: "Sewer Scope" },
  ];

    const [{ user, emailTemplates }, dispatch] = useStateValue();

  const getLockboxData = () => {
    if (formData.lockboxType === "Push Button") return pushButton;
    if (formData.lockboxType === "Electronic") return electronic;
    return [];
  };

  const handleUnassignLockbox = async () => {
    setIsLoad(true);
    try {
      const lockboxId =
        typeof formData.selectedLockbox === "object"
          ? formData.selectedLockbox?.id
          : formData.selectedLockbox;

      const type = formData.lockboxType;

      if (!lockboxId || !type) return;

      const updateFn =
        type === "Push Button"
          ? updatePushButtonLockboxById
          : updateElectronicLockboxById;

      // 🔥 1. Free lockbox
      await updateFn(lockboxId, {
        status: "Available",
        assignedDealId: "",
        assignedDealLabel: "",
      });

      if (dealId) {
        await updateDealById(dealId, {
          selectedLockbox: "",
          lockboxType: "",
        });
      }

      onFormChange("selectedLockbox", "");
      onFormChange("lockboxType", "");

      onFormChange("assignedDealId", "");
      onFormChange("assignedDealLabel", "");
      toast.success("Lockbox unassigned successfully");
      setIsLoad(false);
      onSubmit();
    } catch (err) {
      console.error("Unassign error:", err);
    }
  };

  const options = getLockboxData() || [];

  const handleAddParticipant = () => {
    setParticipants((prev) => [
      ...prev,
      {
        id: Date.now(),
        typeId: "",
        type: "",
        isCustom: true,
        fullName: "",
        phone: "",
        email: "",
        search: {
          fullName: "",
          phone: "",
          email: "",
        },
        dropdown: {
          field: null,
          index: null,
        },
        filtered: [],
      },
    ]);
  };

  const handleRemoveParticipant = (index) => {
    setParticipants((prev) => {
      const removedItem = prev[index];

      const updated = prev.filter((_, idx) => idx !== index);

      if (onContactSelection && removedItem) {
        onContactSelection({
          typeId: removedItem.id,
          remove: true,
        });
      }

      return updated;
    });
  };

  const handleParticipantChange = (index, field, value) => {
    setParticipants((prev) => {
      const updated = prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      );

      const selected = updated[index];

      if (field === "type" && selected && onContactSelection) {
        onContactSelection({
          typeId: selected.id,
          type: value,
          contactId: selected.contactId || "",
        });
      }

      return updated;
    });
  };

  const handleParticipantSearch = (index, field, value) => {
    setParticipants((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;

        const val = value.toLowerCase();
        const list = contactSync || [];

        const filtered = list.filter((c) => {
          const name = (c.name || "").toLowerCase();
          const phone = (c.phone || "").toLowerCase();
          const email = (c.email || "").toLowerCase();

          if (field === "fullName") return name.includes(val);
          if (field === "phone") return phone.includes(val);
          if (field === "email") return email.includes(val);
        });

        return {
          ...item,
          search: {
            ...item.search,
            [field]: value,
          },
          filtered,
          dropdown: {
            field,
            index: item.dropdown?.index ?? null,
          },
        };
      }),
    );
  };

  const handleSelectContact = (index, contact) => {
    setParticipants((prev) => {
      const updated = prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              contactId: contact.id,
              fullName: contact.name || "",
              phone: contact.phone || "",
              email: contact.email || "",
              search: {
                fullName: contact.name || "",
                phone: contact.phone || "",
                email: contact.email || "",
              },
              dropdown: {
                field: null,
                index: null,
              },
              filtered: [],
            }
          : item,
      );

      const selected = updated[index];

      if (selected && onContactSelection) {
        onContactSelection({
          typeId: selected.id,
          type: selected.type || "",
          contactId: contact.id,
        });
      }

      return updated;
    });
  };

  // Use a typed-in value (a professional who isn't an existing contact yet).
  // Clears the contact link so this becomes a manual entry that's saved as-is
  // and synced to the shared Team Directory on save.
  const handleAddCustomParticipant = (index, field, value) => {
    setParticipants((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              [field]: value,
              contactId: "",
              isCustom: true,
              search: { ...item.search, [field]: value },
              dropdown: { field: null, index: null },
              filtered: [],
            }
          : item,
      ),
    );
  };

  // Picked an existing contact from the Add-Client modal → fill the participant.
  const handlePickParticipantContact = async (contactId) => {
    const index = participantPickerFor;
    setParticipantPickerFor(null);
    if (index == null) return;
    try {
      const { getContactsByIds } = await import(
        "../../utils/firebasefunctions"
      );
      const [contact] = await getContactsByIds([contactId]);
      if (contact) handleSelectContact(index, contact);
    } catch (err) {
      console.error("Failed to read picked contact:", err);
      toast.error("Couldn't load that contact");
    }
  };

  // Created a brand-new contact from the modal → save it to Google + fill the
  // participant (it also syncs to the Team Directory when the deal saves).
  const handleCreateParticipantContact = async (form) => {
    const index = participantCreatingFor;
    setParticipantSaving(true);
    try {
      if (!user?.gmailtoken?.access_token) {
        toast.error("Connect Gmail in Settings before adding a new contact");
        return;
      }
      const created = await addContact({ user, contact: form });
      if (index != null) {
        handleSelectContact(index, {
          id: created.firebaseId,
          name: created.name || form.name || "",
          email: created.email || form.email || "",
          phone: created.phone || form.phone || "",
        });
      }
      toast.success("Contact added");
      setParticipantCreatingFor(null);
    } catch (err) {
      console.error("Create participant contact failed:", err);
      toast.error(err.message || "Create failed");
    } finally {
      setParticipantSaving(false);
    }
  };

    // === Vendor contact picker helpers ===
    // Generic apply-contact handler: fills two arbitrary form fields with
    // the picked contact's name + email. Reused across Stager / Inspector
    // / Photographer pickers.
    const makeVendorPickHandler = (
      nameField,
      emailField,
      setPickerOpen,
      labelForToast,
    ) => async (contactId) => {
      setPickerOpen(false);
      try {
        const { getContactsByIds } = await import(
          "../../utils/firebasefunctions"
        );
        const [contact] = await getContactsByIds([contactId]);
        if (contact) {
          if (contact.name) onFormChange(nameField, contact.name);
          if (contact.email) onFormChange(emailField, contact.email);
        }
      } catch (err) {
        console.error("Failed to read picked contact:", err);
        toast.error("Couldn't load that contact");
      }
    };

    // Same fill, but from a Team Directory pro ({ name, email, phone }) —
    // no contact fetch needed.
    const makeVendorDirectoryHandler = (
      nameField,
      emailField,
      setPickerOpen,
    ) => (pro) => {
      setPickerOpen(false);
      if (pro?.name) onFormChange(nameField, pro.name);
      if (pro?.email) onFormChange(emailField, pro.email);
    };

    const makeVendorCreateHandler = (
      nameField,
      emailField,
      setSaving,
      setCreating,
      labelForToast,
    ) => async (form) => {
      setSaving(true);
      try {
        if (!user?.gmailtoken?.access_token) {
          toast.error(
            "Connect Gmail in Settings before adding a new contact",
          );
          return;
        }
        const created = await addContact({ user, contact: form });
        if (created.name) onFormChange(nameField, created.name);
        if (created.email) onFormChange(emailField, created.email);
        toast.success(`${labelForToast} added`);
        setCreating(false);
      } catch (err) {
        console.error("Create vendor contact failed:", err);
        toast.error(err.message || "Create failed");
      } finally {
        setSaving(false);
      }
    };

    const handlePickStager = makeVendorPickHandler(
      "stagingProvider",
      "stagingProviderEmail",
      setStagerPickerOpen,
      "Stager",
    );
    const handleCreateStagerContact = makeVendorCreateHandler(
      "stagingProvider",
      "stagingProviderEmail",
      setStagerSaving,
      setStagerCreating,
      "Stager",
    );

    // Per-inspection vendor + batch-assignment handlers.
    // activeVendorIdx semantics:
    //   "__BATCH__"      → picker is for the multi-select "Confirm" flow
    //   <inspectionKey>  → picker is changing a single inspection's vendor
    const BATCH_KEY = "__BATCH__";

    const setInspectionVendor = (inspectionKey, patch) => {
      const next = {
        ...(formData.inspectionVendors || {}),
        [inspectionKey]: {
          ...((formData.inspectionVendors || {})[inspectionKey] || {}),
          ...patch,
        },
      };
      onFormChange("inspectionVendors", next);
    };

    const clearInspectionVendor = (inspectionKey) => {
      const next = { ...(formData.inspectionVendors || {}) };
      delete next[inspectionKey];
      onFormChange("inspectionVendors", next);
    };

    // The "queue": inspections that are checked but don't yet have a vendor.
    const getInspectionQueue = () =>
      inspectionList.filter(
        (i) =>
          formData.preInspections?.[i.key] &&
          !formData.inspectionVendors?.[i.key],
      );

    // Assign a vendor to every inspection currently in the queue + fire one
    // batch notify email listing all of them.
    const assignBatchAndNotify = (contact) => {
      const queue = getInspectionQueue();
      if (queue.length === 0) return;

      // 1. Write vendor onto each queued inspection.
      const nextVendors = { ...(formData.inspectionVendors || {}) };
      queue.forEach((i) => {
        nextVendors[i.key] = {
          name: contact.name || "",
          email: contact.email || "",
        };
      });
      onFormChange("inspectionVendors", nextVendors);

      // 2. Fire one email listing all of them.
      const labels = queue.map((i) => `• ${i.label}`).join("\n");
      notifyvendor(
        contact.email,
        deal.propertyaddress,
        participants,
        formatDate(deal.closingdate),
        formData.lockboxType,
        selectedLockboxData?.lockboxNumber,
        formData.lockboxType === "Push Button"
          ? selectedLockboxData?.code
          : selectedLockboxData?.pin,
        "inspector",
        formatDate(formData.targetListDate),
        contact.name,
        labels,
      );
    };

    const handlePickInspector = async (contactId) => {
      setInspectorPickerOpen(false);
      const key = activeVendorIdx;
      if (!key) return;
      try {
        const { getContactsByIds } = await import(
          "../../utils/firebasefunctions"
        );
        const [contact] = await getContactsByIds([contactId]);
        if (!contact) return;

        if (key === BATCH_KEY) {
          // Stage the vendor for confirmation — DON'T send yet.
          setPendingInspectionVendor({
            name: contact.name || "",
            email: contact.email || "",
          });
        } else {
          setInspectionVendor(key, {
            name: contact.name || "",
            email: contact.email || "",
          });
        }
      } catch (err) {
        console.error("Failed to read picked inspector contact:", err);
        toast.error("Couldn't load that contact");
      } finally {
        setActiveVendorIdx(null);
      }
    };

    // Inspector picked from the Team Directory ({ name, email, phone }).
    const handlePickInspectorFromDirectory = (pro) => {
      setInspectorPickerOpen(false);
      const key = activeVendorIdx;
      setActiveVendorIdx(null);
      if (!key || !pro) return;
      const vendor = { name: pro.name || "", email: pro.email || "" };
      if (key === BATCH_KEY) setPendingInspectionVendor(vendor);
      else setInspectionVendor(key, vendor);
    };

    const handleCreateInspectorContact = async (form) => {
      const key = activeVendorIdx;
      if (!key) {
        setInspectorCreating(false);
        return;
      }
      setInspectorSaving(true);
      try {
        if (!user?.gmailtoken?.access_token) {
          toast.error(
            "Connect Gmail in Settings before adding a new contact",
          );
          return;
        }
        const created = await addContact({ user, contact: form });

        const newContact = {
          name: created.name || "",
          email: created.email || "",
        };
        if (key === BATCH_KEY) {
          setPendingInspectionVendor(newContact);
        } else {
          setInspectionVendor(key, {
            name: newContact.name,
            email: newContact.email,
          });
        }
        toast.success("Inspector added");
        setInspectorCreating(false);
      } catch (err) {
        console.error("Create inspector contact failed:", err);
        toast.error(err.message || "Create failed");
      } finally {
        setInspectorSaving(false);
        setActiveVendorIdx(null);
      }
    };

    const handlePickPhotographer = makeVendorPickHandler(
      "photographer",
      "photographerEmail",
      setPhotographerPickerOpen,
      "Photographer",
    );
    const handleCreatePhotographerContact = makeVendorCreateHandler(
      "photographer",
      "photographerEmail",
      setPhotographerSaving,
      setPhotographerCreating,
      "Photographer",
    );

    // Maps the legacy `type` argument to the corresponding template id in the
    // emailTemplates board so Henry can edit the wording in Settings.
    const TEMPLATE_FOR_VENDOR_TYPE = {
      consult: "staging-consult-vendor",
      staging: "staging-install-vendor",
      destaging: "staging-destage-vendor",
      photographer: "photographer-vendor",
      inspector: "pre-inspection-vendor",
    };

    // Pretty label per inspection key — used to build the bulleted list in the
    // pre-inspection vendor email.
    const INSPECTION_LABELS = {
      fullInspection: "Full Inspection",
      radon: "Radon",
      sewerScope: "Sewer Scope",
      cityInspection: "City Inspection",
      chimney: "Chimney",
    };

    const buildInspectionList = (preInspections) => {
      if (!preInspections) return "(none selected)";
      const checked = Object.keys(preInspections)
        .filter((k) => preInspections[k])
        .map((k) => `• ${INSPECTION_LABELS[k] || k}`);
      return checked.length ? checked.join("\n") : "(none selected)";
    };

    // Renders + sends a vendor notification email using the user's customized
    // template (with DEFAULT_TEMPLATES fallback). Tokens are substituted
    // client-side and the rendered subject/body/html go to send-email-custom.
    const notifyvendor = async (
      email,
      address,
      clients,
      closingdate,
      lockboxType,
      lockboxNumber,
      lockboxcode,
      type,
      targetListDate,
      receivername,
      // When type === "inspector", this is the single inspection's label
      // (e.g. "Radon"). Overrides the full bulleted list of all checked
      // inspections — relevant since different vendors handle different
      // inspections.
      singleInspection = null,
    ) => {
      try {
        // Flip the matching button to "Sending…" immediately so the click
        // registers instantly, even though the email send itself is a
        // server round-trip we don't control the speed of.
        setNotifyingType(type);
        const templateId = TEMPLATE_FOR_VENDOR_TYPE[type];
        if (!templateId) {
          toast.error(`Unknown vendor notification type: ${type}`);
          return;
        }
        if (!email) {
          toast.error("Vendor email is not set on this deal");
          return;
        }

        const def = DEFAULT_TEMPLATES[templateId] || {};
        const custom = emailTemplates?.[templateId] || {};
        const subjectTpl = custom.subject ?? def.subject ?? "";
        const bodyTpl = custom.body ?? def.body ?? "";

        const installDate =
          type === "staging" ? formatDate(formData.stagingInstall) : "";
        const destageDate =
          type === "destaging" ? formatDate(formData.destaging) : "";
        const photoshootDate =
          type === "photographer" ? formatDate(formData.photographerDate) : "";
        const inspectionList =
          type === "inspector"
            ? (singleInspection ||
                buildInspectionList(formData.preInspections))
            : "";

        // Source the seller list from the deal's real clients. `clients` here is
        // the seller-form participants array, which is empty on deals whose
        // people live under deal.customers (the "Client Information" contacts).
        // When participants has no named entries, resolve deal.customers by id
        // instead so the email lists the actual clients.
        const hasNamedParticipants =
          Array.isArray(clients) &&
          clients.some((c) => (c?.fullName || c?.name || "").trim());
        let sellerContacts = Array.isArray(clients) ? clients : [];
        if (!hasNamedParticipants && deal?.customers?.length) {
          try {
            const resolved = await getContactsByIds(deal.customers);
            if (resolved?.length) sellerContacts = resolved;
          } catch (e) {
            console.error("notifyvendor: couldn't resolve deal clients", e);
          }
        }

        const data = {
          vendorName: receivername || "",
          propertyAddress: address || "",
          // Bulleted "• Name · phone · email" list of the deal's clients, so the
          // {{clients}} slot shows real contact info.
          clients: formatClientsList(sellerContacts),
          closingDate: closingdate || "",
          targetListDate: targetListDate || "",
          installDate,
          destageDate,
          photoshootDate,
          inspectionList,
          lockboxType: lockboxType || "",
          // Electronic lockboxes never expose the serial/code over email.
          lockboxNumber: lockboxType === "Electronic" ? "" : lockboxNumber || "",
          lockboxCode: lockboxType === "Electronic" ? "" : lockboxcode || "",
          lockbox: lockboxLine(lockboxType, lockboxNumber, lockboxcode),
          senderFirstName: user?.firstname || "",
          senderName: `${user?.firstname || ""} ${user?.lastname || ""}`.trim(),
        };

        const subject = interpolate(subjectTpl, data);
        // Strip optional fact lines (target date, lockbox, an empty sellers
        // list) that came out blank, so the email doesn't show empty rows.
        const bodyText = stripEmptyFactLines(interpolate(bodyTpl, data));
        const banner = buildBanner({ propertyAddress: data.propertyAddress }, "");
        const body = paragraphsToHtml(bodyText);
        const signature = buildSignature(user);
        const footer = buildFooter({});
        const html = wrapEmail({ banner, body, signature, footer });

        const res = await authedFetch(
          "https://us-central1-team-edelstein.cloudfunctions.net/api/send-email-custom",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: user.id,
              to: email,
              toName: receivername || "",
              subject,
              body: bodyText, // plain-text fallback
              html,
              type: `vendor-${type}`,
              relatedDealId: dealId,
            }),
          },
        );

        if (res.status === 200) {
          toast.success("Vendor notified");
        } else {
          let msg = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            msg = j?.error || j?.message || msg;
          } catch {
            /* ignore */
          }
          toast.error(msg);
        }
      } catch (error) {
        toast.error("Failed to send email");
        console.error("notifyvendor error:", error);
      } finally {
        setNotifyingType(null);
      }
    };

  const processedOptions = useMemo(() => {
    return [...options]
      .map((item) => ({
        ...item,
        disabled: item.status !== "Available",
      }))
      .sort((a, b) => a.disabled - b.disabled);
  }, [options]);

  const selectedLockboxData =
    formData.lockboxType === "Push Button"
      ? pushButton.find(
          (item) => String(item.id) === String(formData.selectedLockbox),
        )
      : electronic.find(
          (item) => String(item.id) === String(formData.selectedLockbox),
        );

  const isLockboxPersisted =
    !!formData.selectedLockbox &&
    !!formData.lockboxType &&
    !!selectedLockboxData &&
    String(selectedLockboxData.assignedDealId) === String(dealId);

  const isLockboxAssigned = isLockboxPersisted;

  const isLockboxDisabled = isLockboxPersisted;

  return (
    <div className=" px-1 py-3">
      <CollapsibleSection title="Pre-Listing:">

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-4">
        <Field
          label="Target Date/Coming Soon Date"
          type="date"
          isDate = {true}
          field="listingAppointment"
          value={formData.listingAppointment}
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Live Date"
          field="targetListDate"
          value={formData.targetListDate}
          type="date"
                 isDate = {true}
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="Suggested Price"
          field="suggestedPrice"
          value={formData.suggestedPrice}
          onChange={onFormChange}
          isEdit={isEdit}
          type="currency"
          isNumber={true}
        />
        <Field
          label="List Price"
          field="listPrice"
          value={formData.listPrice}
          onChange={onFormChange}
          isEdit={isEdit}
          type="currency"
          isNumber={true}
        />
      </div>

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <div className="relative w-full sm:w-[48%]">
          <p className="text-sm text-gray-500 pb-1">Occupied?</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenStatus(!openStatus)}
                className={`w-full border-2 border-gray-300 rounded-lg px-2 py-1 cursor-pointer ${formData.occupied === "" ? "text-gray-400" : ""}`}
              >
                {formData.occupied || "Select Occupied"}
              </div>

              <EditableSelect
                style={{ top: "60px", left: "0px" }}
                visible={openStatus}
                activateoutsidecontainerclick={() => setOpenStatus(!openStatus)}
                status={Ocuupy}
                onClickcorrect={(value) => {
                  onFormChange("occupied", value);
                  setOpenStatus(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.occupied || "-"}</p>
          )}
        </div>
        <Field
          label="Expiration Date"
          field="expirationdate"
          value={formData.expirationdate}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      <div className="mt-3 rounded-lg">
        <p className="text-sm text-gray-500">OfferEaze</p>

        <div className="flex items-center gap-x-2 mt-1">
          {isEdit ? (
            <>
              <input
                type="checkbox"
                checked={formData.isLinkCreated}
                onChange={(e) =>
                  onFormChange("isLinkCreated", e.target.checked)
                }
                className="w-4 h-4 cursor-pointer"
              />

              <p className="">
                {formData.isLinkCreated ? "Link created" : "No link yet"}
              </p>
            </>
          ) : (
            <p className="">
              {formData.isLinkCreated ? "Link created" : "No link yet"}
            </p>
          )}
        </div>
      </div>

      </CollapsibleSection>

      <CollapsibleSection title="Marketing & Staging:">
      <div className="mt-2 mb-2 flex items-baseline justify-end flex-wrap gap-2">
        {!formData.stagingProviderEmail && !isEdit && (
          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
            Add a Staging Provider Email to enable vendor notifications
          </span>
        )}
      </div>

      {/* --- Stager card --- */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Stager
          </div>
          <button
            type="button"
            onClick={() => setStagerPickerOpen(true)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 border border-slate-300 hover:border-themeColor hover:text-themeColor hover:bg-slate-50 rounded-md px-2 py-0.5 transition"
          >
            <Plus size={11} /> Find or add contact
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
          <MiniSelectCell
            label="Type"
            value={formData.staging}
            placeholder="Not set"
            options={stagingOpt}
            isEdit={isEdit}
            open={openStaging}
            onToggleOpen={() => setOpenStaging(!openStaging)}
            onSelect={(v) => {
              onFormChange("staging", v);
              setOpenStaging(false);
            }}
          />
          <MiniTextCell
            label="Provider"
            value={formData.stagingProvider}
            field="stagingProvider"
            isEdit={isEdit}
            onChange={onFormChange}
          />
          <MiniTextCell
            label="Email"
            value={formData.stagingProviderEmail}
            field="stagingProviderEmail"
            isEdit={isEdit}
            onChange={onFormChange}
          />
        </div>

        {/* Fills Register Redesign's intake form from this file instead of
            retyping it. Accountants never request staging, so they don't see it. */}
        {canRequestStaging(user) && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => setStagingBidOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-[11.5px] font-semibold text-slate-700 transition hover:border-themeColor hover:bg-slate-50 hover:text-themeColor"
            >
              <Mail size={12} /> Request staging bid
            </button>
          </div>
        )}
      </div>

      {/* --- Stage Schedule card --- */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
          Stage Schedule
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
          <ScheduleCell
            label="Consult"
            field="stagingBid"
            value={formData.stagingBid}
            canNotify={!!formData.stagingProviderEmail}
            busy={notifyingType === "consult"}
            isEdit={isEdit}
            onChange={onFormChange}
            onNotify={() =>
              notifyvendor(
                formData.stagingProviderEmail,
                deal.propertyaddress,
                participants,
                formatDate(deal.closingdate),
                formData.lockboxType,
                selectedLockboxData?.lockboxNumber,
                formData.lockboxType === "Push Button"
                  ? selectedLockboxData?.code
                  : selectedLockboxData?.pin,
                "consult",
                formatDate(formData.targetListDate),
                formData.stagingProvider,
              )
            }
          />
          <ScheduleCell
            label="Install"
            field="stagingInstall"
            value={formData.stagingInstall}
            canNotify={!!formData.stagingProviderEmail}
            busy={notifyingType === "staging"}
            isEdit={isEdit}
            onChange={onFormChange}
            onNotify={() =>
              notifyvendor(
                formData.stagingProviderEmail,
                deal.propertyaddress,
                participants,
                formatDate(deal.closingdate),
                formData.lockboxType,
                selectedLockboxData?.lockboxNumber,
                formData.lockboxType === "Push Button"
                  ? selectedLockboxData?.code
                  : selectedLockboxData?.pin,
                "staging",
                formatDate(formData.targetListDate),
                formData.stagingProvider,
              )
            }
          />
          <ScheduleCell
            label="Destage"
            field="destaging"
            value={formData.destaging}
            canNotify={!!formData.stagingProviderEmail}
            busy={notifyingType === "destaging"}
            isEdit={isEdit}
            onChange={onFormChange}
            onNotify={() =>
              notifyvendor(
                formData.stagingProviderEmail,
                deal.propertyaddress,
                participants,
                formatDate(deal.closingdate),
                formData.lockboxType,
                selectedLockboxData?.lockboxNumber,
                formData.lockboxType === "Push Button"
                  ? selectedLockboxData?.code
                  : selectedLockboxData?.pin,
                "destaging",
                formatDate(formData.targetListDate),
                formData.stagingProvider,
              )
            }
          />
        </div>
      </div>

      {/* --- Photography card --- */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Photography
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPhotographerPickerOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 border border-slate-300 hover:border-themeColor hover:text-themeColor hover:bg-slate-50 rounded-md px-2 py-0.5 transition"
            >
              <Plus size={11} /> Find or add contact
            </button>
            {formData.photographerEmail && (
              <button
                type="button"
                disabled={notifyingType === "photographer"}
                onClick={() =>
                  notifyvendor(
                    formData.photographerEmail,
                    deal.propertyaddress,
                    participants,
                    formatDate(deal.closingdate),
                    formData.lockboxType,
                    selectedLockboxData?.lockboxNumber,
                    formData.lockboxType === "Push Button"
                      ? selectedLockboxData?.code
                      : selectedLockboxData?.pin,
                    "photographer",
                    formatDate(formData.targetListDate),
                    formData.photographer,
                  )
                }
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 border border-slate-300 hover:border-themeColor hover:text-themeColor hover:bg-slate-50 rounded-md px-2 py-0.5 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {notifyingType === "photographer" ? (
                  <>
                    <Loader2 size={11} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Mail size={11} /> Notify
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
          <MiniTextCell
            label="Photographer"
            value={formData.photographer}
            field="photographer"
            isEdit={isEdit}
            onChange={onFormChange}
          />
          <MiniDateCell
            label="Date"
            field="photographerDate"
            value={formData.photographerDate}
            isEdit={isEdit}
            onChange={onFormChange}
          />
          <MiniTextCell
            label="Email"
            value={formData.photographerEmail}
            field="photographerEmail"
            isEdit={isEdit}
            onChange={onFormChange}
          />
        </div>
      </div>

      {/* --- Pre-Inspections card ---
        Two-step flow: (1) check the inspections to assign, (2) Pick vendor
        opens the contact picker, (3) Confirm fires one batch email.
        Inspections that already have a vendor render with their assignment
        info inline, separate from the selection flow. */}
      {(() => {
        const queue = inspectionList.filter(
          (i) =>
            formData.preInspections?.[i.key] &&
            !formData.inspectionVendors?.[i.key],
        );
        return (
          <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">
              Pre-Inspections
            </div>

            <div className="space-y-1.5">
              {inspectionList.map((item) => {
                const checked = !!formData.preInspections?.[item.key];
                const vendor = formData.inspectionVendors?.[item.key] || null;
                return (
                  <div
                    key={item.key}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        onFormChange("preInspections", {
                          ...formData.preInspections,
                          [item.key]: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm font-medium w-32 shrink-0">
                      {item.label}
                    </span>

                    {vendor && (
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <div className="flex-1 min-w-0 text-[12px] truncate">
                          <span className="font-semibold text-slate-900">
                            {vendor.name || "(no name)"}
                          </span>
                          {vendor.email && (
                            <span className="text-slate-500">
                              {" "}
                              · {vendor.email}
                            </span>
                          )}
                        </div>
                        {vendor.email && (
                          <button
                            type="button"
                            disabled={notifyingType === "inspector"}
                            onClick={() =>
                              notifyvendor(
                                vendor.email,
                                deal.propertyaddress,
                                participants,
                                formatDate(deal.closingdate),
                                formData.lockboxType,
                                selectedLockboxData?.lockboxNumber,
                                formData.lockboxType === "Push Button"
                                  ? selectedLockboxData?.code
                                  : selectedLockboxData?.pin,
                                "inspector",
                                formatDate(formData.targetListDate),
                                vendor.name,
                                item.label,
                              )
                            }
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 border border-slate-300 hover:border-themeColor hover:text-themeColor hover:bg-slate-50 rounded-md px-2 py-0.5 transition disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Re-send the email for just this inspection"
                          >
                            {notifyingType === "inspector" ? (
                              <>
                                <Loader2 size={11} className="animate-spin" /> Sending…
                              </>
                            ) : (
                              <>
                                <Mail size={11} /> Re-notify
                              </>
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => clearInspectionVendor(item.key)}
                          className="text-slate-400 hover:text-red-600 p-0.5 rounded"
                          title="Remove vendor"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Two-step assign / confirm bar. */}
            {queue.length > 0 && (
              <div className="mt-3 p-3 rounded-md border border-slate-200 bg-slate-50/60">
                <div className="text-[12.5px] text-slate-700 mb-2">
                  Assigning:{" "}
                  <span className="font-semibold">
                    {queue.map((q) => q.label).join(", ")}
                  </span>
                </div>

                {!pendingInspectionVendor ? (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveVendorIdx(BATCH_KEY);
                      setInspectorPickerOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 border border-slate-300 hover:border-slate-500 rounded-md px-3 py-1.5 transition"
                  >
                    <Plus size={12} /> Pick vendor
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-[12.5px] text-slate-700">
                      Vendor:{" "}
                      <span className="font-semibold text-slate-900">
                        {pendingInspectionVendor.name || "(no name)"}
                      </span>{" "}
                      <span className="text-slate-500">
                        · {pendingInspectionVendor.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveVendorIdx(BATCH_KEY);
                          setInspectorPickerOpen(true);
                        }}
                        className="text-[12px] text-slate-500 hover:text-slate-900 underline"
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingInspectionVendor(null)}
                        className="text-[12px] text-slate-500 hover:text-slate-900 underline"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const v = pendingInspectionVendor;
                          if (!v) return;
                          assignBatchAndNotify(v);
                          setPendingInspectionVendor(null);
                        }}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-[#1E293B] hover:opacity-90 rounded-md px-3 py-1.5 transition"
                      >
                        <Mail size={12} /> Confirm & send
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Lockbox — moved here to sit directly under the Pre-Inspections card. */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">
          Lockbox
        </div>

      {isEdit && isLockboxAssigned && (
        <div className="flex items-center justify-end mt-3">
          <button
            onClick={handleUnassignLockbox}
            className="bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white flex gap-x-2 items-center rounded-lg px-4 py-1.5"
          >
            {isLoad ? (
              <svg
                className="w-5 h-5 text-white animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            ) : (
              <LockKeyholeOpen size={15} />
            )}
            Unassigned
          </button>
        </div>
      )}

      <div className={`relative ${isEdit && isLockboxAssigned ? "" : "mt-3"}`}>
        <p className="text-sm text-gray-500 pb-1">Lockbox Type</p>
        {isEdit ? (
          <>
            <div
              onClick={() => {
                if (!isEdit || isLockboxDisabled) return;
                setOpenLockbox(!openLockbox);
              }}
              className={`w-full border-2 border-gray-300 rounded-lg px-2 py-1 ${
                !isEdit || isLockboxDisabled
                  ? "!cursor-not-allowed text-gray-400"
                  : "cursor-pointer"
              }`}
            >
              {formData.lockboxType || "Select Type"}
            </div>

            <EditableSelect
              style={{ top: "60px", left: "0px" }}
              visible={openLockbox}
              activateoutsidecontainerclick={() => setOpenLockbox(false)}
              status={lockboxTypeOptions}
              onClickcorrect={(value) => {
                if (isLockboxDisabled) return;
                onFormChange("lockboxType", value);
                onFormChange("selectedLockbox", "");
                setOpenLockbox(false);
              }}
            />
          </>
        ) : (
          <p className="font-semibold">{formData.lockboxType || "-"}</p>
        )}
      </div>

      <div className="mt-3 rounded-lg">
        <p className="text-sm text-gray-500 pb-2">Assigned Lockbox</p>

        <div className="flex items-center gap-x-4 w-full">
          <div className="w-full relative">
            {isEdit ? (
              <>
                <div
                  onClick={() => {
                    const isDisabled =
                      !formData.lockboxType || isLockboxDisabled;

                    if (isDisabled) return;

                    setOpenAssign((prev) => !prev);
                  }}
                  className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 ${
                    !formData.lockboxType || isLockboxDisabled
                      ? "!cursor-not-allowed text-gray-400"
                      : "cursor-pointer"
                  }`}
                >
                  {selectedLockboxData
                    ? selectedLockboxData.displayLabel
                    : "Select Lockbox"}
                </div>

                <AssignedLockbox
                  style={{ top: "35px", left: "0px" }}
                  visible={openAssign}
                  activateoutsidecontainerclick={() => setOpenAssign(false)}
                  status={processedOptions}
                  onClickcorrect={(item) => {
                    if (item.disabled || isLockboxDisabled) return;

                    onFormChange("selectedLockbox", item.id);
                    setOpenAssign(false);
                  }}
                />
              </>
            ) : (
              <div className="flex items-center gap-x-2">
                <div className="border border-gray-300 bg-gray-100 rounded-lg px-2 py-1 text-xs">
                  <p>{selectedLockboxData?.lockboxNumber || "-"}</p>
                </div>

                <p className="text-sm text-gray-500">Code</p>

                <MaskedField
                  value={
                    formData.lockboxType === "Push Button"
                      ? selectedLockboxData?.code
                      : selectedLockboxData?.pin
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      </CollapsibleSection>

      <CollapsibleSection title="Under Contract Milestones:">

      {/* Financials at the top — Sales Price + Earnest Money Amount
          carried in from turn-in but editable here for post-acceptance
          adjustments. */}
      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="Sales Price"
          field="offerPrice"
          value={formData.offerPrice}
          type="currency"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Earnest Money Amount"
          field="earnestMoney"
          value={formData.earnestMoney}
          type="currency"
          onChange={onFormChange}
          isEdit={isEdit}
        />
      </div>

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="EM Received Date"
          field="earnestMoneyReceivedDate"
          value={formData.earnestMoneyReceivedDate}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
        <Field
          label="Final Acceptance Date"
          field="finalAcceptanceDate"
          value={formData.finalAcceptanceDate}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      {/* Buyer Inspection Yes/No + the inspection date next to it. */}
      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <div className="relative w-full sm:w-[48%]">
          <p className="text-sm text-gray-500 pb-1">Buyer Inspection</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenInspection(!openInspection)}
                className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 cursor-pointer ${formData.inspection === "" ? "text-gray-400" : ""}`}
              >
                {formData.inspection || "Select"}
              </div>

              <EditableSelect
                style={{ top: "75px", left: "0px" }}
                visible={openInspection}
                activateoutsidecontainerclick={() =>
                  setOpenInspection(!openInspection)
                }
                status={yesNo}
                onClickcorrect={(value) => {
                  onFormChange("inspection", value);
                  setOpenInspection(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.inspection || "-"}</p>
          )}
        </div>
        <Field
          label="Buyer Inspection Date"
          field="buyerInspection"
          value={formData.buyerInspection}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      {/* Inspection Deadline is the contingency end date — drives the
          "Inspection Deadline" countdown in Important Dates and the
          inspectionContingencyEnd placeholder used in acceptance
          emails. Sits between the Buyer Inspection row and the
          Rescission row so the two inspection-related dates are
          adjacent. */}
      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="Inspection Deadline"
          field="inspectionDeadline"
          value={formData.inspectionDeadline}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <div className="relative w-full sm:w-[48%]">
          <p className="text-sm text-gray-500 pb-1">Rescission Period</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenRecission(!openRecission)}
                className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 cursor-pointer ${formData.recissionPeriod === "" ? "text-gray-400" : ""}`}
              >
                {formData.recissionPeriod || "Select"}
              </div>

              <EditableSelect
                style={{ top: "75px", left: "0px" }}
                visible={openRecission}
                activateoutsidecontainerclick={() =>
                  setOpenRecission(!openRecission)
                }
                status={recission}
                onClickcorrect={(value) => {
                  onFormChange("recissionPeriod", value);
                  setOpenRecission(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.recissionPeriod || "-"}</p>
          )}
        </div>
        <Field
          label="Rescission Deadline"
          field="rescissionDeadline"
          value={formData.rescissionDeadline}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="Financing Type"
          field="financingType"
          value={formData.financingType}
          type="text"
          onChange={onFormChange}
          isEdit={isEdit}
        />
        <Field
          label="Financing Deadline"
          field="financingDeadline"
          value={formData.financingDeadline}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="Appraisal"
          field="appraisal"
          value={formData.appraisal}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
        <div className="relative w-full sm:w-[48%]">
          <p className="text-sm text-gray-500">Appraisal Complete?</p>
          {isEdit ? (
            <>
              <div
                onClick={() => setOpenAppraisal(!openAppraisal)}
                className={`w-full border-2 border-gray-300 rounded-lg py-1 px-2 cursor-pointer ${formData.appraisalComplete === "" ? "text-gray-400" : ""}`}
              >
                {formData.appraisalComplete || "Select"}
              </div>

              <EditableSelect
                style={{ top: "75px", left: "0px" }}
                visible={openAppraisal}
                activateoutsidecontainerclick={() =>
                  setOpenAppraisal(!openAppraisal)
                }
                status={appraisal}
                onClickcorrect={(value) => {
                  onFormChange("appraisalComplete", value);
                  setOpenAppraisal(false);
                }}
              />
            </>
          ) : (
            <p className="font-semibold">{formData.appraisalComplete || "-"}</p>
          )}
        </div>
      </div>

      {/* Pre-sign Date sits in the bottom row (in place of Final
          Walkthrough, which belongs to the buyer side). Seller-only. */}
      <div className="flex flex-wrap gap-y-3 sm:gap-y-0 sm:justify-between mt-3">
        <Field
          label="Pre-sign Date"
          field="preSignDate"
          value={formData.preSignDate}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
        <Field
          label="Closing Date"
          field="closingdate"
          value={formData.closingdate}
          type="date"
          isDate={true}
          onChange={onFormChange}
          isEdit={isEdit}
          dealAddress={deal?.propertyaddress}
        />
      </div>

      </CollapsibleSection>

      <CollapsibleSection title="Contacts:">

      <div className="flex flex-wrap gap-y-3 justify-between mt-3">
        {participants.map((item, index) => (
          <div
            key={item.id}
            className="w-full sm:w-[49%] border border-gray-300 rounded-xl p-3"
          >
            <div className="flex items-center justify-between relative">
              <div className="flex items-center gap-x-3 w-[85%]">
                <div className="bg-gray-200 rounded-full text-gray-500 font-medium size-9 flex items-center justify-center text-sm uppercase">
                  <p>
                    {isEdit
                      ? item.type
                        ? item.type
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                        : "NA"
                      : item.fullName
                        ? item.fullName
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                        : "NA"}
                  </p>
                </div>

                <div className="flex-1">
                  {isEdit ? (
                    <div className="relative w-full">
                      <div
                        onClick={() =>
                          setOpenTypeDropdown(
                            openTypeDropdown === index ? null : index,
                          )
                        }
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm w-full cursor-pointer bg-white"
                      >
                        {item.type || "Select Type"}
                      </div>

                      <EditableSelect
                        style={{ top: "30px", left: "0px" }}
                        visible={openTypeDropdown === index}
                        activateoutsidecontainerclick={() =>
                          setOpenTypeDropdown(null)
                        }
                        status={contactType
                          .filter((ct) => {
                            const clientType = (
                              ct.clientType || ""
                            ).toLowerCase();
                            return (
                              clientType === "seller" || clientType === "both"
                            );
                          })
                          .map((ct) => ct.type)}
                        onClickcorrect={(value) => {
                          const selectedType = contactType.find(
                            (ct) => ct.type === value,
                          );

                          handleParticipantChange(index, "type", value);
                          handleParticipantChange(
                            index,
                            "typeId",
                            selectedType?.id || item.id,
                          );

                          setOpenTypeDropdown(null);
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-400 mt-0.5">
                        {item.type || "Contact"}
                      </p>
                      <p className="text-sm font-medium text-gray-700">
                        {item.fullName || "-"}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {isEdit && (
                <button
                  onClick={() => handleRemoveParticipant(index)}
                  className="text-red-500 bg-gray-100 absolute top-0 right-0 size-5 flex items-center justify-center rounded-full text-lg font-bold"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="mt-3 space-y-3">
              {isEdit && (
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>

                  <div className="relative">
                    <div
                      onClick={() => setParticipantPickerFor(index)}
                      className="border p-2 w-full rounded cursor-pointer bg-white text-sm min-h-[38px] flex items-center text-gray-700"
                    >
                      {item.fullName || "Select name"}
                    </div>

                    {item.dropdown?.field === "fullName" &&
                      item.dropdown?.index === index && (
                        <ClientSuggesstionPopup
                          style={{ top: "45px", left: "0px" }}
                          fieldtype="name"
                          data={contactSync || []}
                          visible={true}
                          name={item.search?.fullName}
                          phone={item.search?.phone}
                          email={item.search?.email}
                          activateoutsidecontainerclick={() => {
                            setParticipants((prev) =>
                              prev.map((p) =>
                                p.id === item.id
                                  ? {
                                      ...p,
                                      dropdown: {
                                        field: null,
                                        index: null,
                                      },
                                    }
                                  : p,
                              ),
                            );
                          }}
                          onSearch={(val) =>
                            handleParticipantSearch(index, "fullName", val)
                          }
                          onClickcorrect={(c) => handleSelectContact(index, c)}
                          onAddNew={(val) =>
                            handleAddCustomParticipant(index, "fullName", val)
                          }
                        />
                      )}
                  </div>
                </div>
              )}

              <div className={`${!isEdit ? "flex gap-x-16" : ""}`}>
                <p className="text-sm text-gray-500">Phone</p>

                {isEdit ? (
                  <div className="relative">
                    <div
                      onClick={() => setParticipantPickerFor(index)}
                      className="border p-2 w-full rounded cursor-pointer bg-white text-sm min-h-[38px] flex items-center text-gray-700"
                    >
                      {item.phone || "Select phone"}
                    </div>

                    {item.dropdown?.field === "phone" &&
                      item.dropdown?.index === index && (
                        <ClientSuggesstionPopup
                          style={{ top: "45px", left: "0px" }}
                          fieldtype="phone"
                          data={contactSync || []}
                          visible={true}
                          phone={item.search?.phone}
                          activateoutsidecontainerclick={() => {
                            setParticipants((prev) =>
                              prev.map((p) =>
                                p.id === item.id
                                  ? {
                                      ...p,
                                      dropdown: {
                                        field: null,
                                        index: null,
                                      },
                                    }
                                  : p,
                              ),
                            );
                          }}
                          onSearch={(val) =>
                            handleParticipantSearch(index, "phone", val)
                          }
                          onClickcorrect={(c) => handleSelectContact(index, c)}
                          onAddNew={(val) =>
                            handleAddCustomParticipant(index, "phone", val)
                          }
                        />
                      )}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-linkColor">
                    {item.phone || "-"}
                  </p>
                )}
              </div>

              <div className={`${!isEdit ? "flex gap-x-16" : ""}`}>
                <p className="text-sm text-gray-500">Email</p>

                {isEdit ? (
                  <div className="relative">
                    <div
                      onClick={() => setParticipantPickerFor(index)}
                      className="border p-2 w-full rounded cursor-pointer bg-white text-sm min-h-[38px] flex items-center text-gray-700"
                    >
                      {item.email || "Select email"}
                    </div>

                    {item.dropdown?.field === "email" &&
                      item.dropdown?.index === index && (
                        <ClientSuggesstionPopup
                          style={{ top: "45px", left: "0px" }}
                          fieldtype="email"
                          data={contactSync || []}
                          visible={true}
                          email={item.search?.email}
                          activateoutsidecontainerclick={() => {
                            setParticipants((prev) =>
                              prev.map((p) =>
                                p.id === item.id
                                  ? {
                                      ...p,
                                      dropdown: {
                                        field: null,
                                        index: null,
                                      },
                                    }
                                  : p,
                              ),
                            );
                          }}
                          onSearch={(val) =>
                            handleParticipantSearch(index, "email", val)
                          }
                          onClickcorrect={(c) => handleSelectContact(index, c)}
                          onAddNew={(val) =>
                            handleAddCustomParticipant(index, "email", val)
                          }
                        />
                      )}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-linkColor break-all">
                    {item.email || "-"}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isEdit && (
        <div>
          <button
            type="button"
            onClick={() => {
              if (!isEdit) return;
              handleAddParticipant();
            }}
            className={`bg-white flex justify-between items-center gap-x-1.5 border border-gray-300 text-gray-500 font-semibold rounded-lg px-3 py-1.5 mt-4 hover:bg-gray-50 ${
              !isEdit ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Plus size={16} /> Add Participant
          </button>
        </div>
      )}
      </CollapsibleSection>

      {/* Modals below live OUTSIDE the collapsible sections so they
          remain functional regardless of which section is collapsed. */}
      {stagerPickerOpen && (
        <AddClientPicker
          userId={user?.id}
          existingCustomerIds={[]}
          onClose={() => setStagerPickerOpen(false)}
          onPick={handlePickStager}
          includeDirectory
          onPickDirectory={makeVendorDirectoryHandler(
            "stagingProvider",
            "stagingProviderEmail",
            setStagerPickerOpen,
          )}
          onCreateNew={() => {
            setStagerPickerOpen(false);
            setStagerCreating(true);
          }}
        />
      )}

      <RequestStagingBidModal
        open={stagingBidOpen}
        onClose={() => setStagingBidOpen(false)}
        deal={deal}
        formData={formData}
        onFormChange={onFormChange}
        clients={participants}
        currentUser={user}
      />

      {stagerCreating && (
        <ContactEditDrawer
          initial={null}
          onClose={() => !stagerSaving && setStagerCreating(false)}
          onSubmit={handleCreateStagerContact}
          saving={stagerSaving}
        />
      )}

      {participantPickerFor !== null && (
        <AddClientPicker
          userId={user?.id}
          existingCustomerIds={[]}
          onClose={() => setParticipantPickerFor(null)}
          onPick={handlePickParticipantContact}
          includeDirectory
          onPickDirectory={(pro) => {
            const idx = participantPickerFor;
            setParticipantPickerFor(null);
            if (idx == null || !pro) return;
            handleSelectContact(idx, {
              id: "",
              name: pro.name || "",
              phone: pro.phone || "",
              email: pro.email || "",
            });
          }}
          onCreateNew={() => {
            const idx = participantPickerFor;
            setParticipantPickerFor(null);
            setParticipantCreatingFor(idx);
          }}
        />
      )}

      {participantCreatingFor !== null && (
        <ContactEditDrawer
          initial={null}
          onClose={() => !participantSaving && setParticipantCreatingFor(null)}
          onSubmit={handleCreateParticipantContact}
          saving={participantSaving}
        />
      )}

      {inspectorPickerOpen && (
        <AddClientPicker
          userId={user?.id}
          existingCustomerIds={[]}
          onClose={() => setInspectorPickerOpen(false)}
          onPick={handlePickInspector}
          includeDirectory
          onPickDirectory={handlePickInspectorFromDirectory}
          onCreateNew={() => {
            setInspectorPickerOpen(false);
            setInspectorCreating(true);
          }}
        />
      )}

      {inspectorCreating && (
        <ContactEditDrawer
          initial={null}
          onClose={() => !inspectorSaving && setInspectorCreating(false)}
          onSubmit={handleCreateInspectorContact}
          saving={inspectorSaving}
        />
      )}

      {photographerPickerOpen && (
        <AddClientPicker
          userId={user?.id}
          existingCustomerIds={[]}
          onClose={() => setPhotographerPickerOpen(false)}
          onPick={handlePickPhotographer}
          includeDirectory
          onPickDirectory={makeVendorDirectoryHandler(
            "photographer",
            "photographerEmail",
            setPhotographerPickerOpen,
          )}
          onCreateNew={() => {
            setPhotographerPickerOpen(false);
            setPhotographerCreating(true);
          }}
        />
      )}

      {photographerCreating && (
        <ContactEditDrawer
          initial={null}
          onClose={() => !photographerSaving && setPhotographerCreating(false)}
          onSubmit={handleCreatePhotographerContact}
          saving={photographerSaving}
        />
      )}
    </div>
  );
}

export default SellerForm;
